import React from "react";
import BackgroundCover from "../../components/backgroundCover/BackgroundCover";
import TitleWithDescription from "../../components/TitleWithDescription/TitleWithDescription";
import TimelineSection from "../../components/TimelineSection/TimelineSection";
import Title from "../../components/Title/Title";
import { useLanguage } from "../../context/LanguageContext";
import { getClockworkLightTranslations } from "../../translations/clockworkLightTemplate";
import { isMobile, isMobileOrTablet } from "../../utils/appUtils";
import { useEditMode } from "../../context/EditModeContext";
import EditableText from "../../components/EditableText/EditableText";
import { usePageContent } from "../../hooks/usePageContent";
import { ClockworkLightTranslations } from "../../translations/clockworkLightTemplate";

function ClockworkLight() {
  const { language } = useLanguage();
  const { isEditMode } = useEditMode();
  const { content, loading } = usePageContent<ClockworkLightTranslations>('clockwork-light');

  // Use translations as fallback content
  const t = content || getClockworkLightTranslations(language);

  // Show loading state if needed
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin h-12 w-12 border-2 border-gray-400 rounded-full border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="">
      <BackgroundCover
        backgroundHeight={isMobileOrTablet() ? (isMobile() ? 400 : 600) : undefined}
        imagePosition={isMobileOrTablet() ? (isMobile() ? "center" : "initial") : "initial"}
        backgroundImage="/images/clockwork_light_background.png"
      >
        <div className="absolute top-1/2 left-[10%] -translate-y-1/2 z-10 hidden md:block">
          <Title
            firstLine={isEditMode ? (
              <EditableText
                contentKey="clockwork-light.title.firstLine"
                defaultValue={t.title.firstLine}
              />
            ) : t.title.firstLine}
            secondLine={isEditMode ? (
              <EditableText
                contentKey="clockwork-light.title.secondLine"
                defaultValue={t.title.secondLine}
              />
            ) : t.title.secondLine}
            className="hidden md:block"
            animate={true}
          />
        </div>
      </BackgroundCover>

      <div className="mx-auto px-4 py-12 md:py-24">
        <div className="block md:hidden mb-8">
          <Title
            firstLine={isEditMode ? (
              <EditableText
                contentKey="clockwork-light.title.firstLine"
                defaultValue={t.title.firstLine}
              />
            ) : t.title.firstLine}
            secondLine={isEditMode ? (
              <EditableText
                contentKey="clockwork-light.title.secondLine"
                defaultValue={t.title.secondLine}
              />
            ) : t.title.secondLine}
            className=""
            animate={true}
          />
        </div>
        <div className="flex flex-col items-center">
          <div className="max-w-4xl w-full text-sm text-gray-600 leading-relaxed">
            {isEditMode ? (
              <EditableText
                contentKey="clockwork-light.introduction"
                defaultValue={t.introduction}
                multiline={true}
              />
            ) : t.introduction}
          </div>
        </div>
      </div>

      <div className="relative w-full overflow-hidden">
        <div className="w-full bg-gradient-to-b from-[#E1D8D3] via-[#FFFFFF] to-[#E1D8D3] py-12">
          <div className="mx-auto px-4 md:px-12">
            <TitleWithDescription
              title={{
                firstLine: isEditMode ? (
                  <EditableText
                    contentKey="clockwork-light.sections.newTechnology.title.firstLine"
                    defaultValue={t.sections.newTechnology.title.firstLine}
                  />
                ) : t.sections.newTechnology.title.firstLine,
                secondLine: isEditMode ? (
                  <EditableText
                    contentKey="clockwork-light.sections.newTechnology.title.secondLine"
                    defaultValue={t.sections.newTechnology.title.secondLine}
                  />
                ) : t.sections.newTechnology.title.secondLine,
              }}
              description={isEditMode ? (
                <EditableText
                  contentKey="clockwork-light.sections.newTechnology.description"
                  defaultValue={t.sections.newTechnology.description}
                  multiline={true}
                />
              ) : t.sections.newTechnology.description}
              layout="row"
              className="max-w-7xl mx-auto"
            />
          </div>

          <div className="mx-auto px-4 pt-8 max-w-7xl">
            <TimelineSection
              title={{
                firstLine: isEditMode ? (
                  <EditableText
                    contentKey="clockwork-light.sections.journey.title.firstLine"
                    defaultValue={t.sections.journey.title.firstLine}
                  />
                ) : t.sections.journey.title.firstLine,
                secondLine: isEditMode ? (
                  <EditableText
                    contentKey="clockwork-light.sections.journey.title.secondLine"
                    defaultValue={t.sections.journey.title.secondLine}
                  />
                ) : t.sections.journey.title.secondLine,
              }}
              description={isEditMode ? (
                <EditableText
                  contentKey="clockwork-light.sections.journey.description"
                  defaultValue={t.sections.journey.description}
                  multiline={true}
                />
              ) : t.sections.journey.description}
              media={{
                type: 'image',
                src: "/images/clockwork_light_sub1.png",
                alt: "Light Watch Development"
              }}
              mediaPosition="right"
            />

            <TimelineSection
              title={{
                firstLine: isEditMode ? (
                  <EditableText
                    contentKey="clockwork-light.sections.miracle.title.firstLine"
                    defaultValue={t.sections.miracle.title.firstLine}
                  />
                ) : t.sections.miracle.title.firstLine,
                secondLine: isEditMode ? (
                  <EditableText
                    contentKey="clockwork-light.sections.miracle.title.secondLine"
                    defaultValue={t.sections.miracle.title.secondLine}
                  />
                ) : t.sections.miracle.title.secondLine,
              }}
              description={isEditMode ? (
                <EditableText
                  contentKey="clockwork-light.sections.miracle.description"
                  defaultValue={t.sections.miracle.description}
                  multiline={true}
                />
              ) : t.sections.miracle.description}
              media={{
                type: 'image',
                src: "/images/clockwork_light_sub2.png",
                alt: "Light Watch Development"
              }}
              mediaPosition="left"
            />

            <TimelineSection
              title={{
                firstLine: isEditMode ? (
                  <EditableText
                    contentKey="clockwork-light.sections.extracting.title.firstLine"
                    defaultValue={t.sections.extracting.title.firstLine}
                  />
                ) : t.sections.extracting.title.firstLine,
                secondLine: isEditMode ? (
                  <EditableText
                    contentKey="clockwork-light.sections.extracting.title.secondLine"
                    defaultValue={t.sections.extracting.title.secondLine}
                  />
                ) : t.sections.extracting.title.secondLine,
              }}
              description={isEditMode ? (
                <EditableText
                  contentKey="clockwork-light.sections.extracting.description"
                  defaultValue={t.sections.extracting.description}
                  multiline={true}
                />
              ) : t.sections.extracting.description}
              media={{
                type: 'image',
                src: "/images/clockwork_light_patent.png",
                alt: "Light Watch Development"
              }}
              mediaPosition="right"
            />

            <TimelineSection
              title={{
                firstLine: isEditMode ? (
                  <EditableText
                    contentKey="clockwork-light.sections.differentWorlds.title.firstLine"
                    defaultValue={t.sections.differentWorlds.title.firstLine}
                  />
                ) : t.sections.differentWorlds.title.firstLine,
                secondLine: isEditMode ? (
                  <EditableText
                    contentKey="clockwork-light.sections.differentWorlds.title.secondLine"
                    defaultValue={t.sections.differentWorlds.title.secondLine}
                  />
                ) : t.sections.differentWorlds.title.secondLine,
              }}
              description={isEditMode ? (
                <EditableText
                  contentKey="clockwork-light.sections.differentWorlds.description"
                  defaultValue={t.sections.differentWorlds.description}
                  multiline={true}
                />
              ) : t.sections.differentWorlds.description}
              media={{
                type: 'image',
                src: "/images/clockwork_light_sub_enlight.png",
                alt: "Light in Different Worlds Illustration"
              }}
              mediaPosition="left"
            />

            <TimelineSection
              title={{
                firstLine: isEditMode ? (
                  <EditableText
                    contentKey="clockwork-light.sections.space.title.firstLine"
                    defaultValue={t.sections.space.title.firstLine}
                  />
                ) : t.sections.space.title.firstLine,
                secondLine: isEditMode ? (
                  <EditableText
                    contentKey="clockwork-light.sections.space.title.secondLine"
                    defaultValue={t.sections.space.title.secondLine}
                  />
                ) : t.sections.space.title.secondLine,
              }}
              description={isEditMode ? (
                <EditableText
                  contentKey="clockwork-light.sections.space.description"
                  defaultValue={t.sections.space.description}
                  multiline={true}
                />
              ) : t.sections.space.description}
              media={{
                type: 'image',
                src: "/images/clockwork_light_sub_space.jpg",
                alt: "Watch in Space Illustration"
              }}
              mediaPosition="right"
            />

            <TimelineSection
              title={{
                firstLine: isEditMode ? (
                  <EditableText
                    contentKey="clockwork-light.sections.concept.title.firstLine"
                    defaultValue={t.sections.concept.title.firstLine}
                  />
                ) : t.sections.concept.title.firstLine,
                secondLine: isEditMode ? (
                  <EditableText
                    contentKey="clockwork-light.sections.concept.title.secondLine"
                    defaultValue={t.sections.concept.title.secondLine}
                  />
                ) : t.sections.concept.title.secondLine,
              }}
              description={isEditMode ? (
                <EditableText
                  contentKey="clockwork-light.sections.concept.description"
                  defaultValue={t.sections.concept.description}
                  multiline={true}
                />
              ) : t.sections.concept.description}
              media={{
                type: 'image',
                src: "/images/clockwork_light_sub_apple.jpg",
                alt: "Light Movement Concept"
              }}
              mediaPosition="left"
            />

            <TimelineSection
              title={{
                firstLine: isEditMode ? (
                  <EditableText
                    contentKey="clockwork-light.sections.philosophy.title.firstLine"
                    defaultValue={t.sections.philosophy.title.firstLine}
                  />
                ) : t.sections.philosophy.title.firstLine,
                secondLine: isEditMode ? (
                  <EditableText
                    contentKey="clockwork-light.sections.philosophy.title.secondLine"
                    defaultValue={t.sections.philosophy.title.secondLine}
                  />
                ) : t.sections.philosophy.title.secondLine,
              }}
              description={isEditMode ? (
                <EditableText
                  contentKey="clockwork-light.sections.philosophy.description"
                  defaultValue={t.sections.philosophy.description}
                  multiline={true}
                />
              ) : t.sections.philosophy.description}
              media={{
                type: 'image',
                src: "/images/clockwork_light_watch.jpg",
                alt: "Light Watch Philosophy"
              }}
              mediaPosition="right"
              showLine={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClockworkLight;
